import debounce from 'lodash/debounce'
import {
  forwardRef,
  KeyboardEvent,
  MouseEvent,
  ReactNode,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react'

import { IconChevronDown, IconChevronUp } from '@avantstay/backoffice-vectors'

import { CloseButton } from '../CloseButton/CloseButton'
import { ICON_SIZE } from '../constants'
import { PickerPlacement } from '../types'
import { useClassNames } from '../utils/useClassNames'

const BLUR_DEBOUNCE_MS = 200

interface PickerToggleProps {
  children: ReactNode
  hasValue: boolean
  active: boolean
  cleanable: boolean
  disabled?: boolean
  placement: PickerPlacement
  onClean: (event: MouseEvent<HTMLSpanElement>) => void
  onKeyDown: (event: KeyboardEvent<HTMLElement>) => void
}

export const PickerToggle = forwardRef<HTMLDivElement, PickerToggleProps>(
  ({ children, hasValue, active, cleanable, disabled, placement, onClean, onKeyDown }, ref) => {
    const inputRef = useRef<HTMLInputElement>(null)
    const [isFocused, setIsFocused] = useState(false)
    const toggleClassNames = useClassNames('picker-toggle')
    const buttonClassNames = useClassNames('btn')

    const handleFocus = useCallback(() => setIsFocused(true), [])

    const handleBlur = useMemo(
      () =>
        debounce(() => {
          if (document.activeElement !== inputRef.current) {
            setIsFocused(false)
            inputRef.current?.blur()
          }
        }, BLUR_DEBOUNCE_MS),
      [],
    )

    const handleClean = useCallback(
      (event: MouseEvent<HTMLSpanElement>) => {
        event.stopPropagation()
        onClean(event)
        setIsFocused(false)
      },
      [onClean],
    )

    const Caret = placement.startsWith('top') ? IconChevronUp : IconChevronDown
    const { prefix } = toggleClassNames

    return (
      // eslint-disable-next-line jsx-a11y/interactive-supports-focus
      <div
        ref={ref}
        // eslint-disable-next-line jsx-a11y/role-has-required-aria-props
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={active}
        aria-disabled={disabled}
        tabIndex={disabled ? undefined : 0}
        className={toggleClassNames.merge(
          toggleClassNames.withClassPrefix({ active: active || isFocused }),
          buttonClassNames.withClassPrefix('default', { disabled }),
        )}
        onKeyDown={onKeyDown}
        onFocus={disabled ? undefined : handleFocus}
        onBlur={disabled ? undefined : handleBlur}
      >
        <input
          ref={inputRef}
          aria-hidden
          readOnly
          disabled={disabled}
          tabIndex={-1}
          className={prefix('textbox', 'read-only')}
        />
        {children ? (
          <span
            className={prefix(hasValue ? 'value' : 'placeholder')}
            aria-placeholder={typeof children === 'string' ? children : undefined}
          >
            {children}
          </span>
        ) : null}
        {cleanable && hasValue ? (
          <CloseButton className={prefix('clean')} onClick={handleClean} />
        ) : null}
        <Caret className={prefix('caret')} width={ICON_SIZE} height={ICON_SIZE} />
      </div>
    )
  },
)

PickerToggle.displayName = 'PickerToggle'
